import './input.css';
import { useCallback, useEffect, useRef, useState } from 'react';

export default function StringInput({
  value,
  onChange,
  onEnter,
  placeholder,
  allowNewline = true,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  placeholder?: string;
  allowNewline?: boolean;
} & Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'style' | 'onChange' | 'onInput' | 'onKeyDown'
>) {
  const [ogValue] = useState(value);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!inputRef.current) return;
    if (inputRef.current.textContent === value) return;
    inputRef.current.textContent = value;
  }, [value]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key === 'Enter' &&
        !(e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) &&
        onEnter
      ) {
        e.preventDefault();
        onEnter();
        return;
      }

      if (!allowNewline && e.key === 'Enter') {
        e.preventDefault();
      }
    },
    [allowNewline, onEnter],
  );

  return (
    <div
      data-string-input
      data-placeholder={value ? undefined : placeholder}
      contentEditable="plaintext-only"
      suppressContentEditableWarning={true}
      onInput={e => onChange(e.currentTarget.textContent || '')}
      onKeyDown={onKeyDown}
      tabIndex={0}
      ref={inputRef}
      {...props}
    >
      {ogValue}
    </div>
  );
}
